import { CourseAvailability, FeeStatus, StudyLevel, StudyMode } from '../enums.js';
import { ApplyRequirementConfig } from '../offer/index.js';
import { EntryPoint } from '../offer/enums.js';
import { z } from 'zod';
import { ApplicationTaskConfig } from '../config/index.js';
export * from './pg.js';

interface AcademicRequirement {
  name: string;
  academicRequirementId?: string; // Used later for the equivalency lookup
}

// Represents an individual academic entry requirement within a programme (e.g. "120 UCAS tariff points").
// When a programme supports multiple academic requirements, each becomes a separate qualification entry.
export interface AcademicRequirementEntry {
  id: string; // Unique identifier (KSUID)
  name: string; // Display name, e.g. "120 UCAS tariff points or equivalent"
  taskTextOverrides?: Record<string, string>; // Per-task text, e.g. { AcademicTranscript: "College transcript" }
  conditionTextOverrides?: Record<string, string>; // Per-task condition text
}

interface LanguageRequirement {
  name: string;
  languageRequirementId?: string; // Used later for the equivalency lookup
}

// TODO: This needs converting to not have p_key etc at this level so it's a normal CasShieldDBItem where this is data
export interface IApplyCourse {
  s_key: string;
  id: string;
  uni: string;
  shortId: string;
  intakeId: string;
  mode: StudyMode;
  externalId: string;

  name: string;
  shortName: string;
  award: string;
  rqfLevel: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8'; // TODO: Should these just be numbers?
  level: StudyLevel;
  awardingBody?: string;

  duration: number;
  entryPoint: EntryPoint;

  category?: string;
  campus: string;
  faculty: string;
  department?: string;

  isHTQ: boolean;
  UCASApplyCode?: string;
  UCASFacultyCode?: string;

  availableHome: boolean; // TODO: These should probably be Array<FeeStatus> to make it easier to match
  availableOverseas: boolean; // TODO: These should probably be Array<FeeStatus> to make it easier to match

  feeHome?: number;
  feeHomeTotal?: number;
  feeOverseas?: number;
  feeOverseasTotal?: number;

  startMonthHome?: string;
  startDateHome?: string;
  endDateHome?: string;
  startMonthOverseas?: string;
  startDateOverseas?: string;
  endDateOverseas?: string;

  lastAcceptanceDate?: string;

  hecos?: Array<{
    code: string; // TODO: This should be an enum or something
    name: string;
    percentage: number;
  }>;

  // TODO: Settings have just be placed here for now, it needs more thought. The requirements must match in ApplyRegion
  academicRequirements?: {
    [FeeStatus.Home]: AcademicRequirement | AcademicRequirementEntry[] | undefined; // Single (legacy) or array (multi-requirement)
    [FeeStatus.Overseas]: AcademicRequirement | AcademicRequirementEntry[] | undefined; // Single (legacy) or array (multi-requirement)
  };
  languageRequirements?: {
    [FeeStatus.Home]: LanguageRequirement | undefined; // If undefined it is turned off no matter what is in requirements
    [FeeStatus.Overseas]: LanguageRequirement | undefined; // If undefined it is turned off no matter what is in requirements
  };
  requirements: ApplyRequirementConfig;

  // Course availability configuration
  availability?: {
    type: CourseAvailability;
    allowedPartners?: string[]; // Service provider IDs when type is 'limited_partners'
  };

  applicationTasks?: ApplicationTaskConfig;
}

export type ApplyCourse = z.infer<typeof ApplyCourseSchema>;

export type NewApplyCourse = Omit<ApplyCourse, 'p_key' | 's_key'>;

const noSpecialChars = /^[a-zA-Z0-9_ ():,&-]*$/;
const safeString = z.string().regex(noSpecialChars, {
  message: 'Only letters, numbers, _, -, :, ,, &, (,) and spaces are allowed',
});

export const ApplyCourseSchema = z
  .object({
    p_key: safeString.optional(),
    s_key: safeString.optional(),

    username: z.string().optional(),
    lastModified: z.string().optional(),

    shortId: safeString,
    name: safeString,
    shortName: safeString.optional(),

    availableHome: z.boolean(),
    feeHome: z.number().nonnegative().optional(),
    feeHomeTotal: z.number().nonnegative().optional(),
    availableOverseas: z.boolean(),
    feeOverseas: z.number().nonnegative().optional(),
    feeOverseasTotal: z.number().nonnegative().optional(),

    mode: safeString,
    startMonthHome: z.string().optional(),
    duration: z.number().nonnegative(),

    startDateHome: z.string().date().optional(),
    endDateHome: z.string().date().optional(),
    lastApplicationDate: z.string().date().optional(),
    lastAcceptanceDate: z.string().date().optional(),

    award: safeString,
    level: safeString,
    entryPoint: z.nativeEnum(EntryPoint),
    campus: safeString.optional(),

    department: safeString.optional(),
    faculty: safeString.optional(),
    category: safeString.optional(),
    awardingBody: safeString.optional(),
    isHTQ: z.boolean().optional(),

    hecos: z.array(z.object({ code: safeString, percentage: z.number() })).optional(),
    abbreviatedTitle: safeString.optional(),
    UCASApplyCode: safeString.optional(),
    UCASFacultyCode: safeString.optional(),
    availability: z
      .object({
        type: z.nativeEnum(CourseAvailability),
        allowedPartners: z.array(z.string()).optional(),
      })
      .optional(),
  })
  .refine(
    props => {
      return !props.lastAcceptanceDate || !props.startDateHome || props.lastAcceptanceDate >= props.startDateHome;
    },
    {
      path: ['lastAcceptanceDate'],
      message: 'Last acceptance date must be after start date',
    },
  )
  .refine(
    props => {
      return !props.lastAcceptanceDate || !props.endDateHome || props.lastAcceptanceDate <= props.endDateHome;
    },
    {
      path: ['lastAcceptanceDate'],
      message: 'Last acceptance date must be before end date',
    },
  )
  .refine(
    props => {
      let total = 0;
      props.hecos?.map(hecos => {
        total += hecos.percentage;
      });
      return total === 100;
    },
    {
      path: ['hecos'],
      message: 'The HECoS proportion for this course must add up to 100.',
    },
  )
  .refine(
    props => {
      // If availability type is 'limited_partners', allowedPartners must be provided and not empty
      if (props.availability?.type === CourseAvailability.LIMITED_PARTNERS) {
        return props.availability.allowedPartners && props.availability.allowedPartners.length > 0;
      }
      return true;
    },
    {
      path: ['availability', 'allowedPartners'],
      message: 'At least one partner must be selected when limiting availability',
    },
  );
